import type { ApiResponse } from '@/shared/lib';

import type { DocumentRevision } from '../model/types';
import { fetchApprovalItem, fetchVerificationItem } from './homologationItem.api';

export type HomologationItemStage = 'approval' | 'verification';

type HomologationItemResponse = ApiResponse<DocumentRevision>;

const fetchers: Record<
  HomologationItemStage,
  (id: string | number, isRecord: boolean) => Promise<HomologationItemResponse>
> = {
  approval: fetchApprovalItem,
  verification: fetchVerificationItem,
};

const primedItems = new Map<string, Promise<HomologationItemResponse>>();

function buildKey(stage: HomologationItemStage, id: string | number, isRecord: boolean): string {
  return `${stage}:${isRecord ? 'record' : 'document'}:${String(id)}`;
}

/**
 * Starts the request for a homologation item and keeps it for the next `takeHomologationItem` with
 * the same key, so the route guard that checks the item and the page that shows it share one call.
 */
export function primeHomologationItem(
  stage: HomologationItemStage,
  id: string | number,
  isRecord: boolean
): Promise<HomologationItemResponse> {
  const key = buildKey(stage, id, isRecord);
  const promise = fetchers[stage](id, isRecord);
  primedItems.set(key, promise);

  // A failed request must not be served to the page: its own fetch decides how to report it.
  promise.catch(() => {
    if (primedItems.get(key) === promise) {
      primedItems.delete(key);
    }
  });

  return promise;
}

/** Hands over the primed request once, or fetches the item when nothing was primed. */
export function takeHomologationItem(
  stage: HomologationItemStage,
  id: string | number,
  isRecord: boolean
): Promise<HomologationItemResponse> {
  const key = buildKey(stage, id, isRecord);
  const primed = primedItems.get(key);

  if (primed) {
    primedItems.delete(key);
    return primed;
  }

  return fetchers[stage](id, isRecord);
}

export function clearPrimedHomologationItems(): void {
  primedItems.clear();
}
